import React, { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import styles from './crm.module.css'
import { MdCall, MdGroups, MdOutlineFiberNew, MdOutlineThumbDown, MdRepeat, MdSell } from 'react-icons/md'
import { useCrm } from "../../contexts/crmContext"
import { useData } from "../../contexts/dataContext"
import moment from "moment"
import Flag from "../Flag"
import ImageWithFallback from "../ImageWithFallback"
import { formatCurrency, maskCurrency } from "../../utils/formatter"
import { getPropertyForItemInArrayById } from "../../utils/utils"

const Card = ({ item, stageId }) => {
    const [responsibles, setResponsibles] = useState(<></>)
    const [valueProducts, setValueProducts] = useState(<></>)
    const [dragging, setDragging] = useState(false)
    const [responsibleTooltip, setResponsibleTooltip] = useState(null)
    const [showRecentArrival, setShowRecentArrival] = useState(false)
    const { setCardDrag, setOverCard, setOldStage, openModalLead, handleCallToLead, getStatusLeadByLastActivity, voipActive } = useCrm()
    const { origins, flags, allUsersGroup, teams } = useData()

    useEffect(() => {
        getResponsibles()
        getValueProducts()
    }, [item, allUsersGroup])

    useEffect(() => {
        const expiresAt = new Date(item?.recent_until).getTime()
        const remaining = expiresAt - Date.now()
        setShowRecentArrival(Number.isFinite(expiresAt) && remaining > 0)
        if (!Number.isFinite(expiresAt) || remaining <= 0) return undefined

        const timeout = setTimeout(() => setShowRecentArrival(false), remaining)
        return () => clearTimeout(timeout)
    }, [item?.recent_until])

    function dragStart(e) {
        e.dataTransfer.effectAllowed = "move"
        setResponsibleTooltip(null)
        setDragging(true)
        setOldStage(stageId)
        setCardDrag(item)
    }

    function dragEnd() {
        setDragging(false)
        setCardDrag(undefined)
        setOverCard(undefined)
    }

    function dragEnter() {
        setOverCard(item._id)
    }

    const getResponsibles = () => {
        if (item.users?.length > 0) {
            const responsibles = item.users.map((user, index) => {
                const userId = user?.user_id || user
                const userData = allUsersGroup?.find(el => el._id === userId) || (typeof user === "object" ? user : {})
                const name = userData?.name || "Responsável"

                return (
                    <div
                        className={styles.imgProfile}
                        key={`${userId}-${index}`}
                        onMouseEnter={(event) => showResponsibleTooltip(event, name)}
                        onMouseLeave={() => setResponsibleTooltip(null)}
                    >
                        {userData?.image ?
                            <ImageWithFallback
                                src={userData.image}
                                alt={name}
                                functionError={() => getAvatarFallback(name)}
                            />
                            : getAvatarFallback(name)}
                    </div>
                )
            })
            setResponsibles(responsibles)
        } else {
            setResponsibles(<></>)
        }
    }

    const getAvatarFallback = (nameResponsible = "") => {
        const nameArray = nameResponsible?.trim()?.split(" ")?.filter(Boolean) || []
        const initials = nameArray.length > 1 ? `${nameArray[0][0]}${nameArray[nameArray.length - 1][0]}` : (nameArray[0]?.slice(0, 2) || "?")
        return <div className={styles.avatarFallback}>{initials}</div>
    }

    const showResponsibleTooltip = (event, name) => {
        const rect = event.currentTarget.getBoundingClientRect()
        setResponsibleTooltip({
            name,
            top: rect.top - 10,
            left: rect.left + (rect.width / 2)
        })
    }

    function onCallToLead(e) {
        e.stopPropagation()
        handleCallToLead(item)
    }

    const getIconByStatusLead = () => {
        const status = getStatusLeadByLastActivity(item.lastActivity, item.stage_id)
        const color = status === "COOL" ? styles.temperatureCool : (status === "WARM" ? styles.temperatureWarm : styles.temperatureHot)
        const text = status === "COOL" ? "Frio" : (status === "WARM" ? "Esfriando" : "Quente")
        return (
            <div className={`${styles.temperature} ${color}`}>
                <span>{text}</span>
            </div>
        )
    }

    const getValueProducts = () => {
        let sum = 0
        item?.products?.forEach(product => {
            if (!isNaN(product?.value) && parseFloat(product?.value) > 0) {
                sum += parseFloat(product?.value)
            }
        })
        setValueProducts(sum > 0 ? sum.toFixed(2) : 0)
    }

    const saleValue = parseFloat(item?.saleValue || 0)
    const hasSaleValue = !isNaN(saleValue) && saleValue > 0
    const saleBadgeTitle = hasSaleValue ? `Venda marcada: ${formatCurrency(saleValue)}` : "Venda marcada"
    const teamName = getPropertyForItemInArrayById(item?.team_id, teams, "id", "value")

    return (
        <>
            <div className={`${styles.card} ${dragging ? styles.dragging : ''}`} draggable={true} onDragStart={dragStart} onDragEnd={dragEnd} onDragEnter={dragEnter} onClick={() => openModalLead(item._id)} >
                <div className={styles.left}>
                    <div className={styles.groupName}>
                        <span className={styles.name}>{item.name}</span>
                        {showRecentArrival &&
                            <div
                                className={`${styles.recentArrival} ${item?.arrival_type === "reused" ? styles.reusedArrival : ""}`}
                                title={item?.arrival_type === "reused" ? "Lead existente reaproveitado recentemente" : "Lead recebido recentemente"}
                            >
                                <MdOutlineFiberNew />
                                <span>{item?.arrival_type === "reused" ? "Retornou agora" : "Chegou agora"}</span>
                            </div>
                        }
                        {/* Apenas se for um lead repetido, de momento não tem lógica */}
                        {item.repeated &&
                            <div className={styles.repeat}>
                                <MdRepeat />
                            </div>
                        }
                    </div>
                    <span className={styles.business}>{item?.business?.business_name || '-'}</span>
                    <span className={styles.origin}>{getPropertyForItemInArrayById(item.origin_id, origins, "id", "value") || '-'}</span>
                    {teamName &&
                        <div className={styles.teamBadge} title={`Equipe: ${teamName}`}>
                            <MdGroups />
                            <span>{teamName}</span>
                        </div>
                    }
                    {item?.flags?.length > 0 && <div className={styles.flags}>
                        {item?.flags.map((flag, index) => (
                            <Flag
                                name={getPropertyForItemInArrayById(flag, flags, "_id", "name")}
                                color={getPropertyForItemInArrayById(flag, flags, "_id", "color")}
                                key={index}
                            />
                        ))}
                    </div>}
                    <div className={styles.value}>
                        <span>{maskCurrency(valueProducts)}</span>
                    </div>
                    <span className={styles.nextTask}>Próx. tarefa: {(item.nextTask && moment(item.nextTask).format("DD/MM/YYYY HH:mm:ss")) || '-'}</span>
                </div>
                <div className={styles.right}>
                    {getIconByStatusLead()}
                    {item?.hasSale &&
                        <div className={styles.saleBadge} title={saleBadgeTitle} aria-label={saleBadgeTitle}>
                            <MdSell />
                            <span>{hasSaleValue ? formatCurrency(saleValue) : "Venda"}</span>
                        </div>
                    }
                    <div className={styles.responsibles}>
                        {responsibles}
                    </div>

                    <div className={styles.bottom}>
                        {item?.discarted &&
                            <div className={styles.groupDiscarted}>
                                <span className="labelShowInHover">Descartado</span>
                                <div className={styles.discarted}>
                                    <MdOutlineThumbDown />
                                </div>
                            </div>
                        }
                        {voipActive &&
                            <div className={styles.call} onClick={onCallToLead}>
                                <MdCall />
                            </div>
                        }
                    </div>
                </div>
            </div>
            {responsibleTooltip && createPortal(
                <div
                    className={styles.responsibleTooltip}
                    style={{ top: responsibleTooltip.top, left: responsibleTooltip.left }}
                >
                    {responsibleTooltip.name}
                </div>,
                document.body
            )}
        </>
    )
}

export default Card
